import { useState, useEffect, useCallback } from 'react'
import {
  Message,
  ConversationSession,
  createNewSession,
  loadConversations,
  saveSession,
  saveConversations,
  addMessageToSession,
  getCurrentSession,
  setCurrentSession,
  deleteSession,
  clearAllSessions,
  replaceSessionMessages,
  patchSessionMeta,
  replaceOptimisticUserWithCanonicalTurn
} from '../utils/conversationStorage.ts'
import {
  fetchChatConversationsFromApi,
  fetchConversationMessagesFromApi,
  mergeServerConversationsWithLocal
} from '../services/chatConversationService.ts'

export const useConversationHistory = (userId?: string) => {
  const [currentSession, setCurrentSessionState] = useState<ConversationSession | null>(null)
  const [allSessions, setAllSessions] = useState<ConversationSession[]>([])

  useEffect(() => {
    if (!userId) {
      const conversations = loadConversations(userId)
      const session = getCurrentSession(userId)
      setCurrentSessionState(session)
      setAllSessions(conversations.sessions)
      return
    }

    let cancelled = false

    ;(async () => {
      try {
        const server = await fetchChatConversationsFromApi()
        if (cancelled) return
        const local = loadConversations(userId)
        const merged = mergeServerConversationsWithLocal(server, local.sessions)
        const currentId = local.currentSessionId
        const stillValid = Boolean(currentId && merged.some((s) => s.sessionId === currentId))
        saveConversations(
          {
            sessions: merged,
            currentSessionId: stillValid ? currentId : null
          },
          userId
        )
        if (!cancelled) {
          setCurrentSessionState(getCurrentSession(userId))
          setAllSessions(loadConversations(userId).sessions)
        }
      } catch {
        if (!cancelled) {
          const conversations = loadConversations(userId)
          setCurrentSessionState(getCurrentSession(userId))
          setAllSessions(conversations.sessions)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [userId])

  const createSession = useCallback((): ConversationSession => {
    const newSession = createNewSession()
    saveSession(newSession, userId)
    setCurrentSessionState(newSession)
    const conversations = loadConversations(userId)
    setAllSessions(conversations.sessions)
    return newSession
  }, [userId])

  const loadSession = useCallback(
    async (sessionId: string): Promise<ConversationSession | null> => {
      const conversations = loadConversations(userId)
      const session = conversations.sessions.find((s) => s.sessionId === sessionId)

      if (!session) {
        return null
      }

      setCurrentSession(sessionId, userId)
      let updatedConversations = loadConversations(userId)
      let updatedSession = updatedConversations.sessions.find((s) => s.sessionId === sessionId)

      if (!updatedSession) {
        return null
      }

      if (userId && updatedSession.conversationId != null) {
        try {
          const msgs = await fetchConversationMessagesFromApi(String(updatedSession.conversationId))
          const replaced = replaceSessionMessages(sessionId, msgs, userId)
          if (replaced) {
            updatedConversations = loadConversations(userId)
            updatedSession = replaced
          }
        } catch {
        }
      }

      setCurrentSessionState(updatedSession)
      setAllSessions(updatedConversations.sessions)
      return updatedSession
    },
    [userId]
  )

  const syncSessionWithServer = useCallback(
    async (sessionId: string, conversationId: string) => {
      if (!userId) return
      const cid = conversationId.trim()
      if (!/^\d+$/.test(cid)) return
      patchSessionMeta(sessionId, { conversationId: cid }, userId)
      try {
        const msgs = await fetchConversationMessagesFromApi(cid)
        replaceSessionMessages(sessionId, msgs, userId)
      } catch {
        return
      }
      const conversations = loadConversations(userId)
      setAllSessions(conversations.sessions)
      const next = conversations.sessions.find((s) => s.sessionId === sessionId)
      if (next) {
        setCurrentSessionState((prev) => (prev?.sessionId === sessionId ? next : prev))
      }
    },
    [userId]
  )

  const applyCanonicalTurn = useCallback(
    (sessionId: string, userCanonical: Message, assistantCanonical: Message) => {
      const updated = replaceOptimisticUserWithCanonicalTurn(
        sessionId,
        userCanonical,
        assistantCanonical,
        userId
      )
      if (!updated) return
      setAllSessions(loadConversations(userId).sessions)
      setCurrentSessionState((prev) => (prev?.sessionId === sessionId ? updated : prev))
    },
    [userId]
  )

  const addMessage = useCallback(
    (message: Message, targetSessionId?: string): void => {
      let sessionId = targetSessionId

      if (!sessionId) {
        if (currentSession) {
          sessionId = currentSession.sessionId
        } else {
          const conversations = loadConversations(userId)
          if (conversations.currentSessionId) {
            sessionId = conversations.currentSessionId
          }
        }
      }

      if (!sessionId) {
        return
      }

      const updated = addMessageToSession(sessionId, message, userId)
      if (updated) {
        setCurrentSessionState(updated)
        const conversations = loadConversations(userId)
        setAllSessions(conversations.sessions)
      }
    },
    [currentSession, userId]
  )

  const updateCurrentSession = useCallback(
    (updates: Partial<ConversationSession>): void => {
      if (!currentSession) {
        return
      }

      const updated: ConversationSession = {
        ...currentSession,
        ...updates,
        lastUpdated: new Date().toISOString()
      }

      saveSession(updated, userId)
      setCurrentSessionState(updated)
      const conversations = loadConversations(userId)
      setAllSessions(conversations.sessions)
    },
    [currentSession, userId]
  )

  const removeSession = useCallback(
    (sessionId: string): void => {
      deleteSession(sessionId, userId)
      const conversations = loadConversations(userId)
      setAllSessions(conversations.sessions)

      if (currentSession?.sessionId === sessionId) {
        setCurrentSessionState(null)
        setCurrentSession(null, userId)
      }
    },
    [currentSession, userId]
  )

  const clearAll = useCallback((): void => {
    clearAllSessions(userId)
    setCurrentSessionState(null)
    setAllSessions([])
  }, [userId])

  const getSessions = useCallback((): ConversationSession[] => {
    return loadConversations(userId).sessions
  }, [userId])

  const clearCurrentSession = useCallback((): void => {
    if (currentSession) {
      const updated: ConversationSession = {
        ...currentSession,
        isActive: false,
        lastUpdated: new Date().toISOString()
      }
      saveSession(updated, userId)
    }
    setCurrentSessionState(null)
    setCurrentSession(null, userId)
  }, [currentSession, userId])

  return {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    updateCurrentSession,
    removeSession,
    clearAll,
    getSessions,
    clearCurrentSession,
    syncSessionWithServer,
    applyCanonicalTurn
  }
}
